import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

import { useQuery, dehydrate, QueryClient } from 'react-query';

import { setSsrTokens } from 'utils/authentication';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { GetToolkit } from 'services/toolkits';

import UseToast from 'hooks/use-toast';

import { NextSeo } from 'next-seo';
import { getToolkitDynamicProps } from 'router/seoprops';

import ToolkitBanner from 'components/pages/toolkits/toolkitbanner/ToolkitBanner';
import ToolkitWhatsIncluded from 'components/pages/toolkits/toolkitwhatsincluded/ToolkitWhatsIncluded';
import ToolkitAccessCard from 'components/pages/toolkits/toolkitaccesscard/ToolkitAccessCard';
import ToolkitPreview from 'components/pages/toolkits/toolkitpreview/ToolkitPreview';
import Spinner from '@core/components/spinner/Spinner';

import classes from './[...slug].module.scss';

const ToolkitDetailPage = (props) => {
    const router = useRouter();
    const { ToastContainer, toast } = UseToast();
    const seoProps = getToolkitDynamicProps({ ...props });

    const toolkitId = router?.query?.slug?.[0];

    const { data, isLoading } = useQuery(
        ['get-toolkit', toolkitId],
        () => GetToolkit(toolkitId),
        {
            enabled: !!toolkitId,
            cacheTime: 0,
            staleTime: 0,
        }
    );

    const toolkit = data?.data;

    useEffect(() => {
        const purchaseStatus = router.query.purchase;
        if (purchaseStatus !== 'success' && purchaseStatus !== 'cancel') return;

        if (purchaseStatus === 'success') {
            toast('Purchase successful! You now have access to this toolkit.', 'success');
        } else {
            toast('Purchase canceled.', 'info');
        }

        // eslint-disable-next-line no-unused-vars
        const { purchase, ...rest } = router.query;
        router.replace(
            { pathname: router.pathname, query: rest },
            undefined,
            { shallow: true }
        );
    }, [router.query.purchase]);

    return (
        <>
            <NextSeo {...seoProps} />
            <ToastContainer />
            <div className={`w-100 ${classes['page']}`}>
                {(!isLoading && toolkit) ? (
                    <>
                        <div className={classes['banner-wrapper']}>
                            <ToolkitBanner toolkit={toolkit} />
                        </div>

                        <div className={`container ${classes['narrow-container']}`}>
                            <div className={classes['split-row']}>
                                <ToolkitWhatsIncluded html={toolkit.whats_included} />
                                <ToolkitAccessCard toolkit={toolkit} toast={toast} />
                            </div>

                            {toolkit.thumbnail && (
                                <div className={classes['preview-wrapper']}>
                                    <ToolkitPreview
                                        thumbnail={toolkit.thumbnail}
                                        categoryName={toolkit?.category?.name}
                                        hasAccess={toolkit.has_access}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className={classes['loading']}>
                        <Spinner />
                    </div>
                )}
            </div>
        </>
    );
};

export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);
    const toolkitId = context?.params?.slug?.[0];

    if (!toolkitId) {
        return {
            redirect: { permanent: false, destination: '/toolkits' },
        };
    }

    try {
        const response = await GetToolkit(toolkitId);

        // Handle API returning a 200 with error payload "Toolkit not found"
        if (response?.data?.status === 'error') {
            return {
                redirect: { permanent: false, destination: '/404' },
            };
        }

        await queryClient.prefetchQuery(['get-toolkit', toolkitId], () => ({ data: response.data }));

        // Redirect to canonical URL if slug missing or mismatched
        let newQueryParams = '';
        const { slug, ...queryParams } = context.query;
        if (queryParams && Object.keys(queryParams).length > 0) {
            newQueryParams = `?${new URLSearchParams(queryParams).toString()}`;
        }
        const correctSlug = response.data.slug;
        if (!context?.params?.slug?.[1] || context.params.slug[1] !== correctSlug) {
            context.res.setHeader(
                'Location',
                `/toolkits/${toolkitId}/${correctSlug}${newQueryParams}`
            );
            context.res.statusCode = 302;
            context.res.end();
        }

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                id: toolkitId,
                title: response.data.name || '',
                description: response.data.description || '',
                slug: response.data.slug || '',
                image: response.data.thumbnail || '',
            },
        };
    } catch (e) {
        if (e?.response?.status === 404) {
            return {
                redirect: { permanent: false, destination: '/404' },
            };
        }
        return { props: {} };
    }
}

export default ToolkitDetailPage;
